import React from 'react';
import { ArrowDownToLine, ArrowUpFromLine, ArrowLeftRight } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';
import { formatQuantity } from 'core/utils/units';

/**
 * Aktivitaeten — Liste der letzten Lagerbewegungen.
 * Props:
 *   aktivitaeten  { source: string, data: Array<{ typ, matnr, maktx, menge, meins, datum, benutzer }> }
 */
const Aktivitaeten = ({ aktivitaeten }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();

  if (!aktivitaeten) return null;

  const data = Array.isArray(aktivitaeten.data) ? aktivitaeten.data : [];
  const isMock = aktivitaeten.source === 'mock';

  const sectionCls = isDarkMode
    ? 'bg-white/6 border border-white/12 rounded-2xl p-5'
    : 'bg-white border border-slate-200 rounded-2xl p-5 shadow-sm';

  const titleCls = isDarkMode
    ? 'text-sm font-semibold text-white/80 mb-4'
    : 'text-sm font-semibold text-slate-700 mb-4';

  const mockBadge = isDarkMode
    ? 'inline-block text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-amber-500/20 text-amber-300 ml-2'
    : 'inline-block text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-700 ml-2';

  const rowBorder = isDarkMode ? 'border-t border-white/8' : 'border-t border-slate-100';
  const maktxCls = isDarkMode ? 'text-sm text-white/80 truncate' : 'text-sm text-slate-700 truncate';
  const metaCls = isDarkMode ? 'text-xs text-white/40' : 'text-xs text-slate-400';
  const mengeCls = isDarkMode ? 'text-sm text-white/70 whitespace-nowrap' : 'text-sm text-slate-600 whitespace-nowrap';

  const typConfig = {
    wareneingang: {
      label: t('lm.dashActWareneingang'),
      Icon: ArrowDownToLine,
      iconCls: isDarkMode ? 'text-blue-300' : 'text-blue-600',
      bgCls: isDarkMode ? 'bg-blue-500/20' : 'bg-blue-50',
    },
    auslagerung: {
      label: t('lm.dashActAuslagerung'),
      Icon: ArrowUpFromLine,
      iconCls: isDarkMode ? 'text-slate-300' : 'text-slate-500',
      bgCls: isDarkMode ? 'bg-white/10' : 'bg-slate-100',
    },
    umlagerung: {
      label: t('lm.dashActUmlagerung'),
      Icon: ArrowLeftRight,
      iconCls: isDarkMode ? 'text-violet-300' : 'text-violet-600',
      bgCls: isDarkMode ? 'bg-violet-500/20' : 'bg-violet-50',
    },
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const [year, month, day] = iso.split('-');
    return `${day}.${month}.${year}`;
  };

  if (data.length === 0) {
    return (
      <div className={sectionCls}>
        <div className={titleCls}>
          {t('lm.dashAktivitaeten')}
          {isMock && <span className={mockBadge}>{t('lm.mockBadge')}</span>}
        </div>
        <p className={isDarkMode ? 'text-sm text-white/40' : 'text-sm text-slate-400'}>{t('lm.dashEmpty')}</p>
      </div>
    );
  }

  return (
    <div className={sectionCls}>
      {/* Mock-Badge: Mock-Daten muessen sichtbar als solche markiert sein (HANDOVER.md). */}
      <div className={titleCls}>
        {t('lm.dashAktivitaeten')}
        {isMock && <span className={mockBadge}>{t('lm.mockBadge')}</span>}
      </div>
      <div className="flex flex-col">
        {data.map((a, i) => {
          const cfg = typConfig[a.typ] || typConfig.auslagerung;
          const { Icon } = cfg;
          return (
            <div key={i} className={`flex items-center gap-3 py-2.5 ${i > 0 ? rowBorder : ''}`}>
              {/* Typ-Icon */}
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${cfg.bgCls}`}>
                <Icon className={`w-4 h-4 ${cfg.iconCls}`} />
              </div>

              {/* Material-Info */}
              <div className="flex-1 min-w-0">
                <div className={maktxCls}>{a.maktx}</div>
                <div className={metaCls}>
                  <span className="font-mono">{a.matnr}</span>
                  {' · '}
                  <span>{cfg.label}</span>
                  {' · '}
                  <span>{formatDate(a.datum)}</span>
                  {' · '}
                  <span>{a.benutzer}</span>
                </div>
              </div>

              {/* Menge */}
              <div className={mengeCls}>
                {formatQuantity(a.menge, a.meins)} {a.meins}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Aktivitaeten;
